import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchPosts, fetchTagsBySearch } from "../api/posts";
import { readBookmarkedPosts } from "../bookmarks";
import AdShortcode from "./AdShortcode";
import { W as WhatsappIcon } from "../legacy/assets/travel-logo-dark-BGbQ8wfq.js";
import LogoDark from "../legacy/assets/travel-logo-dark-ektEo9yz.png";
import LogoLight from "../legacy/assets/travel-logo-light-CTrjYqKI.svg";
import GDLogo from "../legacy/assets/gdbrandcreative-logo-ELm9x73U.svg";
import { normalizeSiteUrl } from "../utils/siteUrl";

import headerTravel01 from "../legacy/assets/header-travel-01.jpg";
import headerTravel02 from "../legacy/assets/header-travel-02-ByNNQoGE.jpg";
import headerGD from "../legacy/assets/header-gd.jpg";


// ─── Constants ───────────────────────────────────────────────────────────────

const SOCIAL_LINKS = [
  { href: "https://whatsapp.com/channel/0029Vb69sC2JJhzYpd1zRQ1C", icon: "fa-whatsapp" },
  { href: "https://www.instagram.com/travel.rethinkways?igsh=MWEyaWVrZDllZW5lcQ==", icon: "fa-instagram" },
  { href: "https://www.facebook.com/share/1CNm92gSxo/", icon: "fa-facebook-f" },
  { href: "https://x.com/rethinkways", icon: "fa-x-twitter" },
  { href: "https://www.youtube.com/@Travel.Rethinkways", icon: "fa-youtube" },
  { href: "https://www.threads.com/@travel.rethinkways", icon: "fa-threads" },
  { href: "https://in.pinterest.com/travelrethinkways/", icon: "fa-pinterest-p" },
  { href: "https://www.linkedin.com/company/travel-rethink-ways/", icon: "fa-linkedin-in" },
];

const SOCIAL_COLORS = {
  "fa-whatsapp": "text-[#25D366]",
  "fa-instagram": "text-[#E1306C]",
  "fa-facebook-f": "text-[#1877F2]",
  "fa-x-twitter": "text-white",
  "fa-youtube": "text-[#FF0000]",
  "fa-threads": "text-white",
  "fa-pinterest-p": "text-[#E60023]",
  "fa-linkedin-in": "text-[#0A66C2]",
};

const SITE_ORIGIN = "https://travel.rethinkways.com";

const SITE_URLS = {
  home: `${SITE_ORIGIN}/`,
  searchBase: `${SITE_ORIGIN}/`,
  advertiseWithUs: `${SITE_ORIGIN}/advertise-with-us/`,
  wishlist: `${SITE_ORIGIN}/wishlist-favorites/`,
  jobs: `${SITE_ORIGIN}/jobs/`,
};

const NAV_LINKS = [
  { label: "Home", href: `${SITE_ORIGIN}/` },
  { label: "Tour Packages", href: `${SITE_ORIGIN}/travel-tour-packages/` },
  { label: "Super Deals", href: `${SITE_ORIGIN}/super-deals/` },
  { label: "Brand Story", href: `${SITE_ORIGIN}/category/brand-story/` },
  { label: "Jobs",  href: `${SITE_ORIGIN}/jobs/` },
];

const MORE_LINKS = [
  { label: "Advertise With Us", href: `${SITE_ORIGIN}/advertise-with-us/`, icon: "fa-bullhorn" },
  { label: "About Us", href: `${SITE_ORIGIN}/about-us-travel-rethink-ways/`, icon: "fa-user" },
  { label: "Contact Us", href: `${SITE_ORIGIN}/contact-us/`, icon: "fa-phone" },
];

const AD_BANNERS_RAW = [
  {
    href: "https://wa.me/919962610113?text=Hi!%20I%20came%20across%20GD%20Brand%20Creative%20on%20Travel%20Rethink%20Ways%20and%20I'd%20like%20to%20enquire%20about%20your%20creative%20services%20for%20my%20business.",
    imageKey: "gd",
    alt: "GD Brand Creative advertisement offering branding, design and creative services",
    ariaLabel: "Contact GD Brand Creative via WhatsApp",
  },
];

const COPYRIGHT_TEXT = "© 2026 Travel Rethink Ways from";

const EXCLUDED_SEARCH_KEYWORDS = ["job", "jobs", "hospitality"];

const AD_IMAGES = {
  travel01: headerTravel01,
  gd: headerGD,
  travel02: headerTravel02,
};

const AD_BANNERS = AD_BANNERS_RAW.map((banner) => ({
  ...banner,
  image: AD_IMAGES[banner.imageKey] || headerTravel01,
}));

const PRIMARY_HEADER_AD = AD_BANNERS[0];

// ─── Dark Mode Detection ──────────────────────────────────────────────────────

export function getInitialDarkMode() {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("trw-dark-mode");
  if (stored === "1") return true;
  if (stored === "0") return false;
  return (
    document.body.classList.contains("dark") ||
    document.body.classList.contains("jnews-dark-mode") ||
    document.body.classList.contains("trw-dark-mode") ||
    document.documentElement.classList.contains("trw-dark-mode")
  );
}

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function containsExcludedSearchKeyword(value) {
  const normalizedValue = String(value || "").toLowerCase();
  return EXCLUDED_SEARCH_KEYWORDS.some((keyword) => normalizedValue.includes(keyword));
}

function isExcludedSearchPost(post) {
  const title = stripHtml(post?.title?.rendered);
  const excerpt = stripHtml(post?.excerpt?.rendered);
  const link = String(post?.link || "");
  return (
    containsExcludedSearchKeyword(title) ||
    containsExcludedSearchKeyword(excerpt) ||
    containsExcludedSearchKeyword(link)
  );
}

function getSearchResultId(post) {
  return String(post?.__bookmarkId ?? post?.id ?? post?.slug ?? post?.link ?? "");
}

function filterWishlistSearchResults(posts, query) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (!normalizedQuery) return [];

  return posts.filter((post) => {
    if (post?.__bookmarkType === "superdeals") {
      return false;
    }

    if (isExcludedSearchPost(post)) {
      return false;
    }

    const title = stripHtml(post?.title?.rendered);
    const excerpt = stripHtml(post?.excerpt?.rendered);
    return title.toLowerCase().includes(normalizedQuery) || excerpt.toLowerCase().includes(normalizedQuery);
  });
}

function filterWpSearchResults(posts, query) {
  const normalizedQueryRaw = String(query || "").trim().toLowerCase();
  const normalizedQuery = normalizedQueryRaw.replace(/0/g, "o");
  if (!normalizedQuery) return [];

  return posts.filter((post) => {
    if (isExcludedSearchPost(post)) {
      return false;
    }

    // Only show results where JNews post tags contain the query keyword.
    // With `_embed=true`, WP typically includes terms in `_embedded["wp:term"]`.
    const termGroups = post?._embedded?.["wp:term"];
    if (!Array.isArray(termGroups)) return false;

    const tagTerms = termGroups
      .flat()
      .filter((term) => term?.taxonomy === "post_tag" || term?._links?.about?.[0]?.href?.includes("/wp/v2/tags"));

    const prefixes = Array.from({ length: normalizedQuery.length }, (_, i) => normalizedQuery.slice(0, i + 1));
    return tagTerms.some((term) => {
      const tagName = String(term?.name || "").toLowerCase().replace(/0/g, "o");
      return prefixes.some((prefix) => tagName.includes(prefix));
    });
  });
}

function getSearchQueryFromLocationSearch(search = "") {
  const params = new URLSearchParams(search);
  return params.get("q")?.trim() || params.get("search")?.trim() || params.get("s")?.trim() || "";
}

function SearchSuggestions({ isDark, isOpen, isLoading, query, results, onSelect, onViewAll }) {
  if (!isOpen || query.trim().length < 2) return null;

  return (
    <div
      className={`absolute top-full left-0 right-0 z-[120] mt-2 overflow-hidden rounded-2xl border shadow-2xl ${
        isDark ? "border-[#2a3140] bg-[#121722] text-white" : "border-[#d7dce4] bg-white text-black"
      }`}
      style={{ borderRadius: 0, left: 0, width: "450px" }}
    >
      {isLoading ? (
        <div className={`px-4 py-3 text-sm ${isDark ? "text-white/75" : "text-[#5e6775]"}`}>Searching...</div>
      ) : results.length ? (
        <div>
          {results.map((post) => {
            const title = stripHtml(post?.title?.rendered) || "Untitled post";

            return (
              <button
                key={getSearchResultId(post)}
                type="button"
                onClick={() => onSelect(post)}
                className={`block w-full border-b px-4 py-3 text-left transition-colors last:border-b-0 ${
                  isDark
                    ? "border-[#202736] hover:bg-[#1a2230]"
                    : "border-[#eef1f4] hover:bg-[#f7f9fb]"
                }`}
              >
                <div className="text-sm font-semibold leading-5">{title}</div>
              </button>
            );
          })}
          <button
            type="button"
            onClick={onViewAll}
            className={`block w-full px-4 py-3 text-left text-sm font-semibold transition-colors ${
              isDark ? "bg-[#0f1520] text-[#8ec5ff] hover:bg-[#182131]" : "bg-[#f8fafc] text-[#0b63ce] hover:bg-[#eef5ff]"
            }`}
          >
            View all results for "{query.trim()}"
          </button>
        </div>
      ) : (
        <div className={`px-4 py-3 text-sm ${isDark ? "text-white/75" : "text-[#5e6775]"}`}>No matching posts found.</div>
      )}
    </div>
  );
}

// ─── Header Component ─────────────────────────────────────────────────────────

export function Header({ darkMode, onDarkModeChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [internalDarkMode, setInternalDarkMode] = React.useState(getInitialDarkMode);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [stickyVisible, setStickyVisible] = React.useState(false);

  const navRef = React.useRef(null);
  const mobileSearchRef = React.useRef(null);
  const desktopSearchRef = React.useRef(null);
  const isDark = typeof darkMode === "boolean" ? darkMode : internalDarkMode;
  const setIsDark = onDarkModeChange ?? setInternalDarkMode;
  const isHomePage = location.pathname === "/";

  const logoSrc = isDark ? LogoDark : LogoLight;

React.useEffect(() => {
  document.body.classList.toggle("dark", isDark);
  document.body.classList.toggle("jnews-dark-mode", isDark);
  document.body.classList.toggle("trw-dark-mode", isDark);
  document.documentElement.classList.toggle("trw-dark-mode", isDark);

  // 🔥 FORCE background color
  document.body.style.backgroundColor = isDark ? "#000000" : "#ffffff";

  localStorage.setItem("trw-dark-mode", isDark ? "1" : "0");
}, [isDark]);

  // Lock body scroll when mobile menu is open
  React.useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  // Header ad removed (keep logo left-aligned)

  // Show sticky nav when the main nav leaves the viewport without forcing layout on scroll.
  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const isDesktop = window.innerWidth >= 1024;
    if (!isDesktop || !navRef.current) {
      setStickyVisible(false);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setStickyVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(navRef.current);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const query = searchQuery.trim();

    if (query.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchOpen(false);
      return undefined;
    }

    let isCancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setSearchLoading(true);
        const posts = await fetchPosts({
          per_page: 5,
          orderby: "date",
          order: "desc",
          ...(await (async () => {
            const tags = await fetchTagsBySearch(query, { per_page: 20 });
            const tagIds = tags.map((t) => t?.id).filter(Boolean);
            return tagIds.length ? { tags: tagIds.join(",") } : { tags: "0" };
          })()),
        });

        if (!isCancelled) {
          const wpResults = Array.isArray(posts) ? filterWpSearchResults(posts, query) : [];
          const wishlistResults = filterWishlistSearchResults(readBookmarkedPosts(), query);
          const mergedResults = [...wishlistResults, ...wpResults].filter((post, index, allPosts) => {
            const currentId = getSearchResultId(post);
            return allPosts.findIndex((entry) => getSearchResultId(entry) === currentId) === index;
          });

          setSearchResults(mergedResults.slice(0, 5));
          setSearchOpen(true);
        }
      } catch (error) {
        if (!isCancelled) {
          const wishlistResults = filterWishlistSearchResults(readBookmarkedPosts(), query);
          setSearchResults(wishlistResults.slice(0, 5));
          setSearchOpen(true);
        }
      } finally {
        if (!isCancelled) {
          setSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [searchQuery]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;

      if (
        mobileSearchRef.current?.contains(target) ||
        desktopSearchRef.current?.contains(target)
      ) {
        return;
      }

      setSearchOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    setSearchOpen(false);
    setMobileMenuOpen(false);
    navigate(`/?s=${encodeURIComponent(query)}`);
  };

  const handleSearchFocus = () => {
    if (searchQuery.trim().length >= 2) {
      setSearchOpen(true);
    }
  };

  const handleSelectSearchResult = (post) => {
    if (!post?.link) return;
    setSearchOpen(false);
    setMobileMenuOpen(false);
    window.location.href = post.link;
  };

  const handleViewAllResults = () => {
    const query = searchQuery.trim();
    if (!query) return;
    setSearchOpen(false);
    setMobileMenuOpen(false);
    navigate(`/?s=${encodeURIComponent(query)}`);
  };

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const nextQuery = getSearchQueryFromLocationSearch(window.location.search);
    if (nextQuery && nextQuery !== searchQuery) {
      setSearchQuery(nextQuery);
    }
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <header className="trw-header sticky top-0 z-50 [font-family:Poppins,sans-serif] lg:static lg:z-auto">

      {/* ── Mobile Top Bar ── */}
      <div
        className={`trw-mobile-top lg:hidden ${
          isDark ? "border-b border-[#2a2a2a] bg-black" : "border-b border-[#ccc] bg-white"
        }`}
      >
        <div className="trw-container-1024-1500 relative mx-auto flex h-[76px] max-w-[1500px] items-center justify-between px-4">
          {/* Hamburger */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className={`inline-flex h-10 w-10 items-center justify-center ${
              isDark ? "text-white" : "text-black"
            }`}
            aria-label="Open menu"
          >
            <i className="fa-solid fa-bars text-[26px]" />
          </button>

          {/* Centered Logo */}
	          <a
	            href="https://travel.rethinkways.com"
            target="_self"
            rel="noopener noreferrer"
            className="absolute left-1/2 -translate-x-1/2"
          >
            <img
              src={logoSrc}
              alt="Travel Rethink Ways Logo"
              width={134}
              height={67}
              className="trw-header-logo h-auto w-[134px]"
            />
          </a>

          {/* Dark Mode Toggle */}
          <DarkModeToggle isDark={isDark} onChange={setIsDark} size="md" />
        </div>
      </div>



      {/* ── Mobile Drawer Menu ── */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[99999] lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer */}
          <aside className="relative flex h-full w-full flex-col bg-black text-white">
            {/* Close button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-[28px] leading-none text-white"
              aria-label="Close menu"
            >
              ×
            </button>

            {/* Search */}
            <div className="px-5 pt-20">
              <div ref={mobileSearchRef} className="relative">
                <form
                  onSubmit={handleSearch}
                  className="flex h-[44px] items-center rounded-full border border-[#3a3a3a] bg-black px-4"
                >
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={handleSearchFocus}
                    type="text"
                    placeholder="Search..."
                    className="h-full w-full border-0 bg-transparent text-[20px] text-white outline-none placeholder:text-[#9a9a9a]"
                  />
                  <button type="submit" className="text-white" aria-label="Search">
                    <i className="fa-solid fa-magnifying-glass text-[20px]" />
                  </button>
                </form>
                <SearchSuggestions
                  isDark={true}
                  isOpen={searchOpen}
                  isLoading={searchLoading}
                  query={searchQuery}
                  results={searchResults}
                  onSelect={handleSelectSearchResult}
                  onViewAll={handleViewAllResults}
                />
              </div>
            </div>

            {/* Nav links */}
            <nav className="mt-4 border-y border-[#1f1f1f] px-5 py-2">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_self"
                  rel="noopener noreferrer"
                  className="block py-[11px] text-[18px] font-[700] uppercase tracking-[0.3px] text-white"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="https://travel.rethinkways.com/travel-web-stories/"
                target="_self"
                rel="noopener noreferrer"
                className="block py-[11px] text-[18px] font-[700] uppercase tracking-[0.3px] text-white"
              >
                Travel Stories
              </a>
              <a
                href={SITE_URLS.wishlist}
                target="_self"
                rel="noopener noreferrer"
                className="block py-[11px] text-[18px] font-[700] uppercase tracking-[0.3px] text-white"
              >
                Wishlist
              </a>

              {/* "More" links inline on mobile */}
              {MORE_LINKS.map((link) => (
                <a
                  key={`mobile-more-${link.label}`}
                  href={link.href}
                  target="_self"
                  rel="noopener noreferrer"
                  className="block py-[11px] text-[18px] font-[700] uppercase tracking-[0.3px] text-white"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Footer of drawer: socials + copyright */}
            <div className="mt-auto px-5 pb-4">
              <div className="mb-3 flex items-center justify-between">
                {SOCIAL_LINKS.map((social) => (
                  <a
                    key={`m-${social.icon}`}
                    href={social.href}
                    target="_self"
                    rel="noopener noreferrer"
                    className="text-[24px]"
                    aria-label={social.icon.replace(/^fa-/, "").replace(/-/g, " ")}
                  >
                    {social.icon === "fa-whatsapp" ? (
                      <WhatsappIcon className={SOCIAL_COLORS[social.icon] || "text-[#2a2a2a]"} />
                    ) : (
                      <i className={`fa-brands ${social.icon} ${SOCIAL_COLORS[social.icon] || "text-[#2a2a2a]"}`} />
                    )}
                  </a>
                ))}
              </div>

              <div className="flex items-center justify-between border-t border-[#1f1f1f] pt-3">
                <p className="text-[12px] text-white/75">{COPYRIGHT_TEXT}</p>
                <img src={GDLogo} alt="Brand Creative" width={120} height={30} className="h-auto w-[120px]" />
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* ── Desktop Top Bar (social + search + dark toggle) ── */}
      <div className={`hidden text-white lg:block ${isDark ? "bg-[#151618]" : "bg-black"}`}>
        <div className="trw-container-1024-1500 mx-auto flex h-[38px] max-w-[1500px] items-center justify-between px-4">
          {/* Socials */}
          <div className="flex items-center gap-4">
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.icon}
                href={social.href}
                target="_self"
                rel="noopener noreferrer"
                aria-label={social.icon.replace(/^fa-/, "").replace(/-/g, " ")}
                className="inline-flex items-center justify-center text-[14px] leading-none font-normal text-white hover:text-[#cccccc]"
              >
                {social.icon === "fa-whatsapp" ? (
                  <WhatsappIcon className="h-[14px] w-[14px]" />
                ) : (
                  <i className={`fa-brands ${social.icon}`} />
                )}
              </a>
            ))}
          </div>

          {/* Search + Dark Toggle */}
          <div className="flex items-center gap-5">
            <div ref={desktopSearchRef} className="relative">
              <form onSubmit={handleSearch} className="flex items-center gap-3">
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={handleSearchFocus}
                  type="text"
                  placeholder="Search..."
                  className="w-[140px] border-0 bg-transparent text-[14px] text-white outline-none placeholder:text-white/60"
                />
                <button type="submit" aria-label="Search" className="text-white">
                  <i className="fa-solid fa-magnifying-glass text-[14px]" />
                </button>
              </form>
              <SearchSuggestions
                isDark={isDark}
                isOpen={searchOpen}
                isLoading={searchLoading}
                query={searchQuery}
                results={searchResults}
                onSelect={handleSelectSearchResult}
                onViewAll={handleViewAllResults}
              />
            </div>
            <DarkModeToggle isDark={isDark} onChange={setIsDark} size="sm" />
          </div>
        </div>
      </div>

{/* ── Desktop Middle (Logo + Ad) ── */}
      <div className={`trw-desktop-middle hidden lg:block ${isDark ? "bg-black" : "bg-white"}`}>
        <div className="trw-container-1024-1500 mx-auto grid min-h-[170px] max-w-[1500px] grid-cols-1 items-center gap-6 px-4 py-6 lg:grid-cols-[320px_1fr]">
          <a
            href={SITE_URLS.home}
            target="_self"
            rel="noopener noreferrer"
            className="self-center justify-self-center lg:justify-self-start"
          >
            <img
              src={logoSrc}
              alt="Travel Rethink Ways Logo"
              width={250}
              height={125}
              className="trw-header-logo h-auto w-[250px]"
            />
          </a>

          {PRIMARY_HEADER_AD && (
            <div className="flex self-center justify-end">
              <div className="flex items-center justify-end gap-3">
                <span
                  className={`text-[7px] tracking-[2px] ${
                    isDark ? "text-white" : "text-[#000000]"
                  } [writing-mode:vertical-rl] [transform:rotate(180deg)]`}
                >
                  SPONSORED AD
                </span>
                <a
                  href={PRIMARY_HEADER_AD.href}
                  target="_self"
                  rel="noopener noreferrer"
                  aria-label={PRIMARY_HEADER_AD.ariaLabel}
                >
                  <img
                    src={PRIMARY_HEADER_AD.image}
                    alt={PRIMARY_HEADER_AD.alt}
                    width={900}
                    height={110}
                    className="trw-header-ad h-[110px] max-w-full rounded-[10px] object-cover"
                    fetchPriority="high"
                  />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* ── Desktop Main Nav ── */}
      <div
        ref={navRef}
        className={`hidden text-white lg:block ${isDark ? "bg-[#151618]" : "bg-black"}`}
      >
        <div className="trw-container-1024-1500 mx-auto w-full max-w-[1500px] px-4">
          <nav className="float-none flex flex-wrap items-start justify-between md:flex-nowrap">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_self"
                rel="noopener noreferrer"
                className="trw-header-navtab px-6 py-4 text-[14px] font-medium uppercase tracking-[0.4px] text-[#f5f5f5] [font-family:Poppins,Helvetica,Arial,sans-serif] hover:bg-[#3a3a3d]"
              >
                {link.label}
              </a>
            ))}

            <a
              href="https://travel.rethinkways.com/travel-web-stories/"
              target="_self"
              rel="noopener noreferrer"
              className="trw-header-navtab px-6 py-4 text-[14px] font-medium uppercase tracking-[0.4px] text-[#f5f5f5] [font-family:Poppins,Helvetica,Arial,sans-serif] hover:bg-[#3a3a3d]"
            >
              Travel Stories
            </a>

            <a
              href={SITE_URLS.wishlist}
              target="_self"
              rel="noopener noreferrer"
              className="trw-header-navtab flex items-center gap-2 px-6 py-4 text-[14px] font-medium uppercase text-[#f5f5f5] [font-family:Poppins,Helvetica,Arial,sans-serif] hover:bg-[#3a3a3d]"
            >
              <i className="fa-solid fa-heart text-[#f70d28]" />
              Wishlist
            </a>

            {/* More dropdown */}
            <div className="group relative">
              <button
                aria-label="More navigation options"
                className="trw-header-navtab trw-header-more flex min-w-[200px] items-center justify-center gap-1 px-6 py-4 text-[14px] font-medium uppercase text-[#f5f5f5] [font-family:Poppins,Helvetica,Arial,sans-serif] hover:bg-[#3a3a3d]"
              >
                More <i className="fa-solid fa-angle-down text-[14px]" />
              </button>
              <div className="invisible absolute right-0 z-50 mt-0 w-[200px] bg-[#fff] text-black opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
                {MORE_LINKS.map((link, idx) => (
                  <a
                    key={`desktop-more-${link.label}`}
                    href={link.href}
                    target="_self"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 px-5 py-4 text-[14px] hover:bg-[#dfdfdf] ${
                      idx < MORE_LINKS.length - 1 ? "border-b border-[#d7d7d7]" : ""
                    }`}
                  >
                    <i className={`fa-solid ${link.icon} text-[14px]`} />
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* ── Sticky Nav (Desktop, appears after scrolling past main nav) ── */}
      <div
        className={`fixed top-0 right-0 left-0 z-[70] hidden border-b backdrop-blur-sm transition-all duration-300 lg:block ${
          isDark
            ? "border-[#232833] bg-[#000] text-white"
            : "border-[#dedede] bg-white text-black"
        } ${stickyVisible ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-full opacity-0"}`}
      >
        <div className="trw-container-1024-1500 mx-auto flex h-[74px] w-full max-w-[1500px] items-center justify-between gap-6 px-4">
          {/* Sticky Logo */}
	          <a href="https://travel.rethinkways.com" target="_self" rel="noopener noreferrer" className="shrink-0">
            <img
              src={logoSrc}
              alt="Travel Rethink Ways Logo"
              width={142}
              height={71}
              className="trw-header-logo h-auto w-[142px]"
            />
          </a>

          {/* Sticky Nav Links */}
          <nav className="trw-header-sticky-nav flex min-w-0 flex-1 self-stretch items-stretch justify-center">
            <div className="flex h-full items-stretch">
              {NAV_LINKS.map((link) => (
                <a
                  key={`sticky-${link.label}`}
                  href={link.href}
                  target="_self"
                  rel="noopener noreferrer"
                  className={`trw-sticky-navtab inline-flex h-full items-center px-5 text-[14px] font-medium uppercase tracking-[0.3px] ${
                    isDark ? "text-white hover:bg-[#4a4a4a]" : "text-black hover:bg-[#f3f3f3]"
                  }`}
                >
                  {link.label}
                </a>
              ))}

              <a
                href="https://travel.rethinkways.com/travel-web-stories/"
                target="_self"
                rel="noopener noreferrer"
                className={`trw-sticky-navtab inline-flex h-full items-center px-5 text-[14px] font-medium uppercase tracking-[0.3px] ${
                  isDark ? "text-white hover:bg-[#4a4a4a]" : "text-black hover:bg-[#f3f3f3]"
                }`}
              >
                Travel Stories
              </a>

              <a
                href={SITE_URLS.wishlist}
                target="_self"
                rel="noopener noreferrer"
                className={`trw-sticky-navtab inline-flex h-full items-center gap-2 px-5 text-[14px] font-medium uppercase ${
                  isDark ? "text-white hover:bg-[#4a4a4a]" : "text-black hover:bg-[#f3f3f3]"
                }`}
              >
                <i className="fa-solid fa-heart text-[#f70d28]" />
                Wishlist
              </a>

              {/* Sticky More dropdown */}
              <div className="group relative">
                <button
                  aria-label="More navigation options"
                  className={`trw-sticky-more inline-flex h-full w-[200px] items-center justify-center gap-2 px-6 text-[14px] font-medium uppercase ${
                    isDark ? "text-white hover:bg-[#4a4a4a]" : "text-black hover:bg-[#f3f3f3]"
                  }`}
                >
                  More <i className="fa-solid fa-angle-down text-[12px]" />
                </button>
                <div
                  className={`invisible absolute right-0 top-full z-50 mt-0 w-[200px] opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100 ${
                    isDark ? "bg-[#121722] text-white" : "bg-[#fff] text-black"
                  }`}
                >
                  {MORE_LINKS.map((link, idx) => (
                    <a
                      key={`sticky-more-${link.label}`}
                      href={link.href}
                      target="_self"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-3 px-5 py-4 text-[14px] ${
                        isDark ? "text-white hover:bg-[#4a4a4a]" : "text-black hover:bg-[#dfdfdf]"
                      } ${
                        idx < MORE_LINKS.length - 1
                          ? isDark
                            ? "border-b border-[#2a3140]"
                            : "border-b border-[#d7d7d7]"
                          : ""
                      }`}
                    >
                      <i className={`fa-solid ${link.icon} text-[14px]`} />
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* Sticky Dark Toggle */}
          <div className="flex shrink-0 items-center gap-5">
            <DarkModeToggle isDark={isDark} onChange={setIsDark} size="sm-alt" />
          </div>
        </div>
      </div>
    </header>
  );
}

// ─── Dark Mode Toggle Sub-component ──────────────────────────────────────────

/**
 * Reusable dark mode toggle.
 * @param {{ isDark: boolean, onChange: (v: boolean) => void, size: 'md' | 'sm' | 'sm-alt' }} props
 */
function DarkModeToggle({ isDark, onChange, size = "md" }) {
  const trackClass =
    size === "md"
      ? `relative h-8 w-12 rounded-full transition-colors ${isDark ? "bg-[#111827]" : "bg-[#d7d7d7]"}`
      : size === "sm"
      ? `relative h-7 w-12 rounded-full transition-colors ${isDark ? "bg-[#111827]" : "bg-[#65656a]"}`
      : `relative h-7 w-11 rounded-full transition-colors ${isDark ? "bg-[#2d3442]" : "bg-[#d8dbe1]"}`;

  const thumbClass =
    size === "md"
      ? `absolute top-[2px] left-[2px] flex h-7 w-7 items-center justify-center rounded-full bg-[#efefef] transition-transform ${
          isDark ? "translate-x-4 text-[#111827]" : "text-[#5b5b5b]"
        }`
      : size === "sm"
      ? `absolute top-[2px] left-[2px] flex h-[24px] w-[24px] items-center justify-center rounded-full bg-[#efefef] transition-transform ${
          isDark ? "translate-x-5 text-[#111827]" : "text-[#5b5b5b]"
        }`
      : `absolute top-[2px] left-[2px] flex h-[24px] w-[24px] items-center justify-center rounded-full bg-white text-[#697182] shadow-sm transition-transform ${
          isDark ? "translate-x-4" : ""
        }`;

  return (
    <label
      className="inline-flex cursor-pointer items-center"
      title={isDark ? "Dark mode on" : "Dark mode off"}
    >
      <input
        type="checkbox"
        checked={isDark}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
        aria-label={`Dark mode toggle (${isDark ? "on" : "off"})`}
      />
      <span className={trackClass}>
        <span className={thumbClass}>
          <i
            className={`fa-regular ${
              isDark ? "fa-sun text-[#f59e0b]" : "fa-moon text-[#606060]"
            } text-[12px]`}
          />
        </span>
      </span>
    </label>
  );
}

export default Header;
